import { orderPriceKey as priceKey } from "../../orderPrice";
import clsx from "clsx";
import { format } from "date-fns";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { Account, MsgExecute, MsgMulti, priceFormatter } from "rujira.js";
import {
  Button,
  Decimal,
  Fiat,
  IconDenom,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TableColumn,
  TablePagination,
  assetLabel,
  compareBigInts,
  compareNullableBigInts,
  compareNullableDates,
  compareNumbers,
  compareStrings,
  nFormatter,
  normalizeBigIntDecimals,
  toFiatAmount,
  TranslationProvider,
  useGlobalModalContext,
  useIsTouchDevice,
  useTablePagination,
  useTableSort,
  useTranslation,
} from "rujira.ui";

import { Icons } from "rujira.ui";
import {
  BalanceProvider,
  usePreloadedBalance,
} from "../../../common/components/Balance";
import {
  MsgProvider,
  TxBalanceAccountButton,
  TxButton,
} from "../../../common/components/TxButton";
import { useIsBelow } from "../../../hooks/useBreakpoint";
import { useHoverTip } from "../../../hooks/useHoverTip";
import { usePreloadedAccountData } from "../../../services/accountData";
import { useAccounts } from "../../../services/accounts";
import { TradeBreakpoint } from "../../static";
import { useTradeBookSubscription } from "../../TradeSubscriptions";
import { useTradeContext } from "../Context";
import { InputQuantity } from "../InputQuantity";
import { LimitOrderRow } from "../mobile/LimitOrderRow";
import { OrderRows } from "../mobile/OrderRow";
import {
  LimitOrdersAccountFragment$data,
  LimitOrdersAccountFragment$key,
} from "./__generated__/LimitOrdersAccountFragment.graphql";
import {
  LimitOrdersFragment$data,
  LimitOrdersFragment$key,
} from "./__generated__/LimitOrdersFragment.graphql";
import { Nav, OrderLink } from "./Common";
import { useOrdersContext } from "./Context";
import { LabelSide, LabelType, Price, StatusFilled } from "./shared/labels";
import { OrderCategory, selectOrderCategory } from "./orderCategory";

type FinOrderNode = Extract<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<LimitOrdersAccountFragment$data["fin"]>["ordersV2"]
      >["edges"]
    >[number]
  >["node"],
  { __typename: "FinOrder" }
>;
type LimitOrdersData = {
  all: FinOrderNode[];
  open: FinOrderNode[];
  filled: FinOrderNode[];
  total: number;
};

type TableProps = FC<{
  expanded?: boolean;
  rows?: boolean;
  orders: FinOrderNode[];
  layout?: "portfolio" | "trade";
  category?: OrderCategory;
}>;

type LimitOrder = LimitOrdersFragment$data[number];

const LIMIT_SORT_KEYS = [
  "pair",
  "side",
  "date",
  "currentPrice",
  "orderPrice",
  "amount",
  "remaining",
  "value",
  "filled",
  "unclaimed",
  "fee",
] as const;

type LimitSortKey = (typeof LIMIT_SORT_KEYS)[number];

type LimitColumn = TableColumn & {
  sortKey?: LimitSortKey;
  label?: (t: ReturnType<typeof useTranslation>["t"]) => string;
  align?: "left" | "center" | "right";
  trade?: Pick<TableColumn, "width" | "minWidth">;
};

// Portfolio baselines and Trade differences live beside each header definition.
const LIMIT_COLUMNS: readonly LimitColumn[] = [
  { id: "logo", width: "2rem" },
  {
    id: "pair",
    sortKey: "pair",
    label: (t) => t("pair"),
    align: "left",
    width: "6.5rem",
    minWidth: "6.5rem",
    trade: { width: "6.2rem", minWidth: "4.2rem" },
  },
  {
    id: "side",
    sortKey: "side",
    label: (t) => t("side"),
    align: "center",
    width: "3.5rem",
    minWidth: "3.5rem",
    trade: { width: "3rem", minWidth: "3rem" },
  },
  {
    id: "date",
    sortKey: "date",
    label: (t) => t("date"),
    align: "left",
    width: "5.5rem",
    minWidth: "5.5rem",
  },
  {
    id: "current-price",
    sortKey: "currentPrice",
    label: (t) => t("currentPrice"),
    align: "right",
    width: "auto",
    minWidth: "7rem",
  },
  {
    id: "order-price",
    sortKey: "orderPrice",
    label: (t) => t("orderPrice"),
    align: "right",
    width: "auto",
    minWidth: "8rem",
    trade: { width: "9%", minWidth: "7rem" },
  },
  {
    id: "amount",
    sortKey: "amount",
    label: (t) => t("amount"),
    align: "right",
    width: "auto",
    minWidth: "6rem",
  },
  {
    id: "remaining",
    sortKey: "remaining",
    label: (t) => t("remaining"),
    align: "right",
    width: "auto",
    minWidth: "6rem",
    trade: { width: "7%", minWidth: "5.5rem" },
  },
  {
    id: "value",
    sortKey: "value",
    label: (t) => t("value"),
    align: "right",
    width: "auto",
    minWidth: "6rem",
  },
  {
    id: "filled",
    sortKey: "filled",
    label: (t) => t("filled"),
    align: "center",
    width: "6rem",
    minWidth: "6rem",
    trade: { width: "4rem", minWidth: "4rem" },
  },
  {
    id: "unclaimed",
    sortKey: "unclaimed",
    label: (t) => t("unclaimed"),
    align: "right",
    width: "auto",
    minWidth: "6rem",
  },
  {
    id: "fee",
    sortKey: "fee",
    label: (t) => t("fee"),
    align: "right",
    width: "auto",
    minWidth: "6rem",
  },
  { id: "actions", width: "5rem", minWidth: "5rem" },
];
const LIMIT_HEADERS = LIMIT_COLUMNS.flatMap(({ sortKey, align, label }) =>
  sortKey && label ? [{ value: sortKey, align, label }] : []
);
const TRADE_LIMIT_COLUMNS = LIMIT_COLUMNS.map(({ trade, ...column }) => ({
  ...column,
  ...trade,
}));

const limitPairLabel = (order: LimitOrder) =>
  `${assetLabel(order.pair.assetBase)}/${assetLabel(order.pair.assetQuote)}`;

// Amount and Remaining are in base units; Value is in quote units on either side.
const limitBalance = (
  order: LimitOrder,
  key: "amount" | "remaining" | "value"
) => ({
  amount:
    key === "value"
      ? order.side === "base"
        ? order.offerValue
        : order.offer
      : key === "remaining"
        ? order.side === "base"
          ? order.remaining
          : order.remainingValue
        : order.side === "base"
          ? order.offer
          : order.offerValue,
  asset: key === "value" ? order.pair.assetQuote : order.pair.assetBase,
});

const limitBalanceSortValue = (
  order: LimitOrder,
  key: "amount" | "remaining" | "value"
) => {
  const { amount, asset } = limitBalance(order, key);
  return normalizeBigIntDecimals(amount, asset.metadata.decimals);
};

const limitFillPercentage = (order: LimitOrder) =>
  order.offer
    ? Number(((order.offer - order.remaining) * 10_000n) / order.offer)
    : 0;

const compareLimitOrders = (
  a: LimitOrder,
  b: LimitOrder,
  sortBy: LimitSortKey,
  sortDir: SortDir
) => {
  switch (sortBy) {
    case "pair":
      return compareStrings(limitPairLabel(a), limitPairLabel(b), sortDir);
    case "side":
      return compareStrings(a.side, b.side, sortDir);
    case "date":
      return compareNullableDates(a.updatedAt, b.updatedAt, sortDir);
    case "currentPrice":
      return compareNullableBigInts(
        a.pair.book.center,
        b.pair.book.center,
        sortDir
      );
    case "orderPrice":
      return compareBigInts(a.rate, b.rate, sortDir);
    case "amount":
      return compareBigInts(
        limitBalanceSortValue(a, "amount"),
        limitBalanceSortValue(b, "amount"),
        sortDir
      );
    case "remaining":
      return compareBigInts(
        limitBalanceSortValue(a, "remaining"),
        limitBalanceSortValue(b, "remaining"),
        sortDir
      );
    case "value":
      return compareBigInts(a.valueUsd, b.valueUsd, sortDir);
    case "filled":
      return compareNumbers(
        limitFillPercentage(a),
        limitFillPercentage(b),
        sortDir
      );
    case "unclaimed":
      return compareBigInts(
        normalizeBigIntDecimals(
          a.filled,
          (a.side === "base" ? a.pair.assetQuote : a.pair.assetBase).metadata
            .decimals
        ),
        normalizeBigIntDecimals(
          b.filled,
          (b.side === "base" ? b.pair.assetQuote : b.pair.assetBase).metadata
            .decimals
        ),
        sortDir
      );
    case "fee":
      return compareBigInts(
        normalizeBigIntDecimals(
          a.filledFee,
          (a.side === "base" ? a.pair.assetQuote : a.pair.assetBase).metadata
            .decimals
        ),
        normalizeBigIntDecimals(
          b.filledFee,
          (b.side === "base" ? b.pair.assetQuote : b.pair.assetBase).metadata
            .decimals
        ),
        sortDir
      );
  }
};

enum Tab {
  Open = "open",
  Filled = "filled",
  History = "history",
}

const context = createContext<{
  tab: Tab;
  setTab: (v: Tab) => void;
}>({
  tab: Tab.Open,
  setTab: () => {},
});

const trackingContext = createContext({
  tab: Tab.Open,
  setTab: (_tab: Tab) => {},
});

export const Context: FC<PropsWithChildren<{ category?: OrderCategory }>> = ({
  children,
  category = "limit",
}) => {
  const [tab, setTab] = useState<Tab>(Tab.Open);
  const Provider = (category === "tracking" ? trackingContext : context)
    .Provider;
  return <Provider value={{ tab, setTab }}>{children}</Provider>;
};

export const TrackingContext: FC<PropsWithChildren> = ({ children }) => (
  <Context category="tracking">{children}</Context>
);

const accountFragment = graphql`
  fragment LimitOrdersAccountFragment on Account {
    fin {
      ordersV2(first: 100)
        @connection(key: "TradeSubscriptionsFragment_ordersV2") {
        edges {
          node {
            __typename
            ... on FinOrder {
              id
              pair {
                address
              }
              type
              deviation
              side
              rate
              remaining
              filled
              ...LimitOrdersFragment
            }
          }
        }
      }
    }
  }
`;

export const useLimitOrderCategoriesData = (): Record<
  OrderCategory,
  LimitOrdersData
> => {
  const { accountData } = usePreloadedAccountData();
  const { only } = useOrdersContext();
  const { address } = useTradeContext();
  const d = useFragment<LimitOrdersAccountFragment$key>(
    accountFragment,
    accountData
  );

  return useMemo(() => {
    const finOrders =
      d?.fin?.ordersV2?.edges
        ?.map((o) => o?.node)
        .filter(
          (node): node is FinOrderNode =>
            !!node && node.__typename === "FinOrder"
        ) || [];

    const filtered = finOrders.filter(
      (order) => !address || !only || order.pair.address === address
    );
    return {
      limit: selectOrderCategory(filtered, "limit"),
      tracking: selectOrderCategory(filtered, "tracking"),
    };
  }, [d, address, only]);
};

export const Tabs: FC<{ data?: LimitOrdersData; category?: OrderCategory }> = ({
  data,
  category = "limit",
}) => {
  const { tab, setTab } = useContext(
    category === "tracking" ? trackingContext : context
  );
  return (
    <Nav
      tab={tab}
      setTab={setTab}
      tabs={[
        { value: Tab.Open, count: data?.open.length || 0 },
        { value: Tab.Filled, count: data?.filled.length || 0 },
      ]}
    />
  );
};

export const Content: FC<{
  category?: OrderCategory;
  data: LimitOrdersData;
}> = ({ category = "limit", data }) => {
  const { tab } = useContext(
    category === "tracking" ? trackingContext : context
  );
  const { open, filled } = data;
  const rows = useIsBelow(TradeBreakpoint);

  switch (tab) {
    case Tab.Open:
      return (
        <Table rows={rows} orders={open} layout="trade" category={category} />
      );
    case Tab.Filled:
      return (
        <Table rows={rows} orders={filled} layout="trade" category={category} />
      );
    case Tab.History:
      return <History />;
  }
};

const groupOrders = (
  account: Account,
  orders: FinOrderNode[],
  amount?: string
) =>
  Array.from(
    orders.reduce((grouped, order) => {
      const entry = [order.side, priceKey(order), amount ?? null] as const;
      const entries = grouped.get(order.pair.address);
      if (entries) entries.push(entry);
      else grouped.set(order.pair.address, [entry]);
      return grouped;
    }, new Map<string, Array<readonly [FinOrderNode["side"], ReturnType<typeof priceKey>, string | null]>>())
  ).map(
    ([contract, contractOrders]) =>
      new MsgExecute(account, [], contract, {
        order: [contractOrders, null],
      })
  );

const useBulkMsgs = (orders: FinOrderNode[]) => {
  const { selected } = useAccounts();
  const { address } = useTradeContext();

  const claimAll = useMemo(() => {
    if (!selected) return null;
    if (!address) return null;

    const toClaim = orders.filter((o) => o.filled);

    if (!toClaim.length) return null;
    const account = Account.fromAddress(selected.address);
    return new MsgMulti(account, groupOrders(account, toClaim));
  }, [orders, address, selected]);

  const cancelAll = useMemo(() => {
    if (!address) return null;
    if (!selected) return null;
    const toCancel = orders.filter((o) => o.remaining);
    if (!toCancel.length) return null;
    const account = Account.fromAddress(selected.address);
    return new MsgMulti(account, groupOrders(account, toCancel, "0"));
  }, [orders, address, selected]);
  return { claimAll, cancelAll };
};

const BulkButtons: FC<{ orders: FinOrderNode[]; compact?: boolean }> = ({
  orders,
  compact,
}) => {
  const { t } = useTranslation();
  const tip = useHoverTip(TradeBreakpoint);
  const { claimAll, cancelAll } = useBulkMsgs(orders);
  const size = compact ? "w-2.5 h-2.5" : "w-3 h-3";

  return (
    <div className="flex ai-c gap-0.5">
      <MsgProvider msg={cancelAll}>
        <TxButton
          hideSimulation
          className={`block transparent ${size} color-grey hover-white`}
          aria-label={t("cancelAll")}
          data-tooltip-content={tip && t("cancelAll")}
          data-tooltip-id={tip}
          data-tooltip-float={false}>
          <Icons.CirclesXMark className={`block ${size}`} />
        </TxButton>
      </MsgProvider>
      <MsgProvider msg={claimAll}>
        <TxButton
          hideSimulation
          className={`block transparent ${size} color-grey hover-white`}
          aria-label={t("claimAll")}
          data-tooltip-content={tip && t("claimAll")}
          data-tooltip-id={tip}
          data-tooltip-float={false}>
          <Icons.ClaimAll className={`block ${size}`} />
        </TxButton>
      </MsgProvider>
    </div>
  );
};

export const BulkActions: FC<{ data: LimitOrdersData }> = ({ data }) => {
  const { tab } = useContext(context);
  const { open, filled } = data;
  const { address } = useTradeContext();
  const orders = tab === Tab.Open ? open : tab === Tab.Filled ? filled : [];

  if (!address || !orders.length) return null;

  return <BulkButtons orders={orders} compact />;
};

export const Table: TableProps = ({
  orders,
  rows,
  layout = "portfolio",
  category = "limit",
}) => {
  const { t } = useTranslation();
  const { tab } = useContext(
    category === "tracking" ? trackingContext : context
  );
  const isTouchDevice = useIsTouchDevice();
  const { address } = useTradeContext();
  const data = useFragment<LimitOrdersFragment$key>(
    orderFragment,
    orders as LimitOrdersFragment$key
  );
  const { claimAll, cancelAll } = useBulkMsgs(orders);
  const { sortBy, sortDir, toggleSort } = useTableSort<LimitSortKey>({
    initialBy: "orderPrice",
    initialDir: SortDir.Asc,
    defaultDir: (key) =>
      ["pair", "side"].includes(key) ? SortDir.Asc : SortDir.Desc,
  });
  const sortedOrders = useMemo(
    () =>
      [...data].sort((a, b) => {
        const comparison = compareLimitOrders(a, b, sortBy, sortDir);
        return comparison || a.pair.address.localeCompare(b.pair.address);
      }),
    [data, sortBy, sortDir]
  );
  const { pageRows: visibleOrders, pagination } = useTablePagination(
    sortedOrders,
    { resetKey: `${category}:${tab}:${sortBy}:${sortDir}` }
  );
  const sortHeaderProps = { active: sortBy, dir: sortDir, onSort: toggleSort };

  if (rows) {
    return (
      <div className="trade__orders-table mt-2">
        {orders.length > 0 ? (
          <OrderRows>
            {data.map((order, index) => (
              <LimitOrderItemView key={orders[index].id} d={order} rows />
            ))}
          </OrderRows>
        ) : (
          <p className="condensed fs-14 color-grey text-center mt-2">
            {t("noOrdersMessage", { label: tab })}
          </p>
        )}
      </div>
    );
  }
  return data.length > 0 ? (
    <>
      <TableFrame
        columns={layout === "trade" ? TRADE_LIMIT_COLUMNS : LIMIT_COLUMNS}
        className="trade__orders-table mt-2"
        stickyFirstColumn
        stickyLastColumn
        tableClassName={clsx({
          "table table--fixed nowrap table--spaced": true,
          "table--no-hover": isTouchDevice,
        })}>
        <thead>
          <tr>
            <th className="text-left" aria-hidden="true" />
            {LIMIT_HEADERS.map(({ value, align, label }) => (
              <SortableTableHeader
                key={value}
                {...sortHeaderProps}
                value={value}
                label={label(t)}
                align={align}
              />
            ))}
            {address ? (
              <th scope="col">
                <div className="flex jc-e w-full">
                  <MsgProvider msg={cancelAll}>
                    <TxButton
                      hideSimulation
                      className={clsx({
                        "block transparent w-3 h-3 color-grey": true,
                        "opacity-10": false,
                        "hover-white": true,
                      })}
                      data-tooltip-content={t("cancelAll")}
                      data-tooltip-id="global-tip"
                      data-tooltip-float={false}>
                      <Icons.CirclesXMark className="block w-3 h-3" />
                    </TxButton>
                  </MsgProvider>
                  <MsgProvider msg={claimAll}>
                    <TxButton
                      hideSimulation
                      className={clsx({
                        "block transparent w-3 h-3 color-grey ml-0.5": true,
                        "opacity-10": false,
                        "hover-white": true,
                      })}
                      data-tooltip-content={t("claimAll")}
                      data-tooltip-id="global-tip"
                      data-tooltip-float={false}>
                      <Icons.ClaimAll className="block w-3 h-3" />
                    </TxButton>
                  </MsgProvider>
                </div>
              </th>
            ) : (
              <th scope="col" />
            )}
          </tr>
        </thead>
        <tbody>
          {visibleOrders.map((order) => (
            <LimitOrderTableItem
              key={`${order.pair.address}:${order.side}:${order.type}:${order.deviation ?? order.rate}`}
              order={order}
            />
          ))}
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  ) : (
    <p className="condensed fs-14 color-grey text-center mt-2">
      {t("noOrdersMessage", { label: tab })}
    </p>
  );
};

export const TableFallback: FC<{ layout?: "portfolio" | "trade" }> = ({
  layout = "portfolio",
}) => {
  const { t } = useTranslation();
  const isTouchDevice = useIsTouchDevice();

  return (
    <TableFrame
      className="trade__orders-table mt-2"
      columns={layout === "trade" ? TRADE_LIMIT_COLUMNS : LIMIT_COLUMNS}
      stickyFirstColumn
      stickyLastColumn
      tableClassName={clsx({
        "table table--fixed nowrap table--spaced": true,
        "table--no-hover": isTouchDevice,
      })}>
      <thead>
        <tr>
          <th className="text-left" aria-hidden="true" />
          {LIMIT_HEADERS.map(({ value, align, label }) => (
            <th key={value} className={`text-${align}`}>
              {label(t)}
            </th>
          ))}
          <th />
        </tr>
      </thead>
      <tbody>
        {[...Array(3)].map((_, i) => (
          <tr key={`skeleton-${i}`}>
            <td className="text-left">
              <div className={clsx({ lp: true, small: true })}>
                <div className="icon-denom skeleton h-2 w-2 br-2" />
                <div className="icon-denom skeleton h-2 w-2 br-2" />
              </div>
            </td>
            {LIMIT_HEADERS.map(({ value, align }) => (
              <td key={value} className={`text-${align}`}>
                <div
                  className={clsx(
                    "skeleton h-1 br-2 inline-block",
                    value === "side" ? "w-5" : "w-10"
                  )}
                />
              </td>
            ))}
            <td>
              <div className="flex jc-e w-full" style={{ gap: "0.5rem" }}>
                <div className="icon-denom skeleton h-2 w-2 br-2" />
                <div className="icon-denom skeleton h-2 w-2 br-2" />
                <div className="skeleton h-2 w-2 br-2 inline-block" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
};

const orderFragment = graphql`
  fragment LimitOrdersFragment on FinOrder @relay(plural: true) {
    deviation
    filled
    filledValue
    filledFee
    offer
    owner
    pair {
      address
      assetBase {
        asset
        chain
        type
        metadata {
          symbol
          decimals
        }
      }
      assetQuote {
        asset
        chain
        type
        metadata {
          symbol
          decimals
        }
        price {
          current
        }
      }
      book {
        id
        center
      }
    }
    remaining
    remainingValue
    rate
    side
    type
    updatedAt
    offerValue
    valueUsd
  }
`;

const LimitOrderTableItem: FC<{ order: LimitOrder }> = ({ order }) => (
  <LimitOrderItemView d={order} />
);

const LimitOrderItemView: FC<{ d: LimitOrder; rows?: boolean }> = ({
  d,
  rows,
}) => {
  const { t } = useTranslation();
  const tip = useHoverTip(TradeBreakpoint);
  const { showModal, hideModal } = useGlobalModalContext();
  const { selected } = useAccounts();
  const contract = d.pair.address;
  const assetAsk = d.side === "base" ? d.pair.assetQuote : d.pair.assetBase;
  const closed = Number(d.remaining) === 0;

  // Keyed on the order's identity, not `d`: every fill replaces `d`, and a new
  // msg re-simulates the claim button.
  const { side, type, deviation, rate } = d;
  const address = selected?.address;

  const cancelMsg = useMemo(() => {
    if (!address) return null;
    if (!contract) return null;

    return new MsgExecute(Account.fromAddress(address), [], contract, {
      order: [[[side, priceKey({ type, deviation, rate }), "0"]], null],
    });
  }, [address, contract, side, type, deviation, rate]);

  const claimMsg = useMemo(() => {
    if (!address) return null;
    if (!contract) return null;

    return new MsgExecute(Account.fromAddress(address), [], contract, {
      order: [[[side, priceKey({ type, deviation, rate }), null]], null],
    });
  }, [address, contract, side, type, deviation, rate]);

  const confirmCancel = () => {
    showModal({
      backgroundClose: false,
      hideCloseButton: true,
      children: cancelMsg ? (
        <TranslationProvider namespace="trade">
          <MsgProvider msg={cancelMsg}>
            <CancelLimitOrderModal hideModal={hideModal} />
          </MsgProvider>
        </TranslationProvider>
      ) : null,
    });
  };

  const editOrder = () => {
    showModal({
      title: t("editOrder"),
      backgroundClose: false,
      children: (
        <TranslationProvider namespace="trade">
          <BalanceProvider
            asset={d.side === "base" ? d.pair.assetBase : d.pair.assetQuote}
            additionalBalance={BigInt(d.remaining)}>
            <EditLimitOrder order={d} hideModal={hideModal} />
          </BalanceProvider>
        </TranslationProvider>
      ),
    });
  };

  const actions = (
    <>
      {d.remaining ? (
        <button
          className="block transparent w-3 h-3 color-grey hover-white"
          onClick={editOrder}
          data-tooltip-content={t("editOrder")}
          data-tooltip-id="global-tip">
          <Icons.CirclePen className="block w-3 h-3" />
        </button>
      ) : null}
      <button
        className={clsx({
          "block transparent w-3 h-3 color-grey": true,
          "opacity-10": Number(d.remaining) === 0,
          "hover-white": Number(d.remaining) > 0,
        })}
        data-tooltip-content={t("cancelOrder")}
        data-tooltip-id="global-tip"
        onClick={d.remaining ? confirmCancel : () => null}
        disabled={!d.remaining}>
        <Icons.CircleXMark className="block w-3 h-3" />
      </button>
    </>
  );

  const claimButton =
    Number(d.filled) > 0 ? (
      <MsgProvider msg={claimMsg}>
        <TxButton
          className="block transparent w-3 h-3"
          aria-label={t("claimOrder")}
          data-tooltip-content={tip && t("claimOrder")}
          data-tooltip-id={tip}
          data-tooltip-float={false}
          hideSimulation
          debounceSimulation={false}>
          <Icons.ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
        </TxButton>
      </MsgProvider>
    ) : undefined;

  if (rows) {
    return <LimitOrderRow d={d} actions={actions} claimButton={claimButton} />;
  }

  return (
    <tr>
      <OrderLink assetBase={d.pair.assetBase} assetQuote={d.pair.assetQuote} />
      <td className="text-center">
        <LabelSide {...d} />
      </td>
      <td className="mono fs-12">
        {format(d.updatedAt, "dd/MM/yy")}{" "}
        <span className="color-grey">{format(d.updatedAt, "HH:mm")}</span>
      </td>
      <td className="text-right mono">
        <Decimal
          subscript
          decimals={12}
          round={4}
          amount={BigInt(d.pair.book.center || 0)}
          symbol={d.pair.assetQuote.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          clip
        />
      </td>
      <td className="text-right">
        <Price {...d} />
      </td>
      {(["amount", "remaining", "value"] as const).map((key) => {
        const { amount, asset } = limitBalance(d, key);
        return (
          <td key={key} className="text-right">
            <Decimal
              subscript
              round={4}
              amount={BigInt(amount)}
              symbol={asset.metadata.symbol}
              symbolClassName="decimal__symbol--small color-grey condensed fw-500"
              clip
            />
          </td>
        );
      })}
      <td className="text-center color-teal">
        <StatusFilled {...d} />
      </td>
      <td className="text-right">
        {d.filled ? (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.filled)}
            symbol={assetAsk.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
            clip
          />
        ) : (
          <span className="color-grey">&mdash;</span>
        )}
      </td>
      <td className="text-right">
        <Decimal
          subscript
          round={4}
          amount={BigInt(d.filledFee)}
          symbol={assetAsk.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          clip
        />
      </td>
      <td>
        <div className="flex jc-e w-full">
          {closed ? (
            <>
              <button
                type="button"
                disabled
                aria-label={t("editOrder")}
                className="block transparent w-3 h-3 color-grey opacity-10">
                <Icons.CirclePen className="block w-3 h-3" />
              </button>
              <button
                type="button"
                disabled
                aria-label={t("cancelOrder")}
                className="block transparent w-3 h-3 color-grey opacity-10">
                <Icons.CircleXMark className="block w-3 h-3" />
              </button>
              {Number(d.filled) > 0 ? (
                <MsgProvider msg={claimMsg}>
                  <TxButton
                    className="block transparent w-3 h-3"
                    data-tooltip-content={t("claimOrder")}
                    data-tooltip-id="global-tip"
                    data-tooltip-float={false}
                    hideSimulation>
                    <Icons.ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
                  </TxButton>
                </MsgProvider>
              ) : (
                <button
                  type="button"
                  disabled
                  aria-label={t("claimOrder")}
                  className="block transparent w-3 h-3 color-grey opacity-10">
                  <Icons.Claim className="block w-3 h-3" />
                </button>
              )}
            </>
          ) : (
            <>
              <button
                className="block transparent w-3 h-3 color-grey hover-white"
                onClick={editOrder}
                data-tooltip-content={t("editOrder")}
                data-tooltip-id="global-tip">
                <Icons.CirclePen className="block w-3 h-3" />
              </button>
              <button
                className="block transparent w-3 h-3 color-grey hover-white"
                data-tooltip-content={t("cancelOrder")}
                data-tooltip-id="global-tip"
                onClick={confirmCancel}>
                <Icons.CircleXMark className="block w-3 h-3" />
              </button>
              {Number(d.filled) > 0 ? (
                <MsgProvider msg={claimMsg}>
                  <TxButton
                    className="block transparent w-3 h-3"
                    data-tooltip-content={t("claimOrder")}
                    data-tooltip-id="global-tip"
                    data-tooltip-float={false}
                    hideSimulation>
                    <Icons.ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
                  </TxButton>
                </MsgProvider>
              ) : (
                <button
                  type="button"
                  disabled
                  className="block transparent w-3 h-3 color-grey opacity-10"
                  aria-label={t("claimOrder")}>
                  <Icons.Claim className="block w-3 h-3" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const PriceUsdSuffix: FC<{
  tokenPrice: bigint;
  quotePrice?: bigint | null;
}> = ({ tokenPrice, quotePrice }) => {
  const usdValue =
    tokenPrice > 0n && quotePrice && quotePrice > 0n
      ? toFiatAmount(quotePrice, tokenPrice)
      : undefined;

  if (!usdValue) return null;

  return (
    <span className="color-grey condensed fs-12 fw-500 ml-0.5">
      {"("}
      <Fiat
        amount={usdValue}
        decimals={12}
        symbol="$"
        padSymbol={false}
        as="span"
      />
      {")"}
    </span>
  );
};

const EditOrderPriceValue: FC<{
  price: bigint;
  quoteSymbol: string;
  quotePrice?: bigint | null;
}> = ({ price, quoteSymbol, quotePrice }) => (
  <div className="edit-limit-order__price-value mt-0.5 color-white">
    <Decimal
      subscript
      decimals={12}
      round={4}
      amount={price}
      symbol={quoteSymbol}
      symbolClassName="decimal__symbol--small color-grey condensed fw-500"
      clip
      as="span"
    />
    <PriceUsdSuffix tokenPrice={price} quotePrice={quotePrice} />
  </div>
);

const EditCurrentPriceValue: FC<{
  price: bigint;
  quoteSymbol: string;
  quotePrice?: bigint | null;
}> = ({ price, quoteSymbol, quotePrice }) => (
  <div className="edit-limit-order__price-value color-grey mt-0.5">
    <span className="decimal">
      {priceFormatter(price) || "0"}
      <span className="decimal__symbol decimal__symbol--small color-grey condensed fw-500">
        {quoteSymbol}
      </span>
    </span>
    <PriceUsdSuffix tokenPrice={price} quotePrice={quotePrice} />
  </div>
);

const EditOrderDeviationValue: FC<{ deviation: bigint }> = ({ deviation }) => {
  const { t } = useTranslation();
  const value = `${deviation > 0n ? "+" : ""}${(
    Number(deviation) / 100
  ).toLocaleDecimal(2)}`;

  return (
    <div className="edit-limit-order__deviation-value mt-0.5">
      <span className="color-white mono">{value}%</span>{" "}
      <span className="color-grey">{t("ofCurrentPrice")}</span>
    </div>
  );
};

const editOrderTxKey = (
  amount: bigint,
  selectedAddress?: string,
  nativeDenom?: string
) => [amount.toString(), selectedAddress ?? "", nativeDenom ?? ""].join(":");

export const History = () => {
  const { t } = useTranslation();
  return (
    <div className="trade__orders-table table-sticky table-sticky--left mt-2">
      <p className="condensed fs-14 color-grey text-center mt-2">
        {t("comingSoon")}
      </p>
    </div>
  );
};

const EditLimitOrder: FC<{
  order: LimitOrder;
  hideModal: () => void;
}> = ({ order: d, hideModal }) => {
  const { t } = useTranslation();
  useTradeBookSubscription(d?.pair.book.id);
  const { selected } = useAccounts();

  const orderPrice = BigInt(d.rate);
  const currentPrice = BigInt(d.pair.book.center || 0);
  const quotePrice = d.pair.assetQuote.price?.current;
  const quoteSymbol = d.pair.assetQuote.metadata.symbol;
  const deviation =
    d.type === "fixed" || d.deviation == null ? null : d.deviation;

  const assetOffer = d.side === "base" ? d.pair.assetBase : d.pair.assetQuote;
  const assetAsk = d.side === "base" ? d.pair.assetQuote : d.pair.assetBase;
  const { balance } = usePreloadedBalance();
  const originalAmount = BigInt(d.remaining);
  const [amount, setAmount] = useState(originalAmount);
  const [draftAmount, setDraftAmount] = useState(originalAmount);
  const funds = amount - originalAmount;
  const draftFunds = draftAmount - originalAmount;
  const selectedAccountAddress = selected?.address;
  const selectedAddress = selectedAccountAddress?.toString();
  const nativeDenom = balance?.asset.variants?.native?.denom;
  const coins = useMemo(
    () =>
      funds > 0n && nativeDenom
        ? [
            {
              denom: nativeDenom,
              amount: funds.toString(),
            },
          ]
        : [],
    [funds, nativeDenom]
  );
  const txKey = editOrderTxKey(amount, selectedAddress, nativeDenom);

  const commitAmount = (nextAmount: bigint) => {
    setDraftAmount(nextAmount);
    if (nextAmount === amount) return;

    setAmount(nextAmount);
  };

  const msg = useMemo(() => {
    if (amount === originalAmount) return null;
    if (!selectedAccountAddress) return null;

    return new MsgExecute(
      Account.fromAddress(selectedAccountAddress),
      coins,
      d.pair.address,
      {
        order: [[[d.side, priceKey(d), amount.toString()]], null],
      }
    );
  }, [
    amount,
    coins,
    d.deviation,
    d.pair.address,
    d.rate,
    d.side,
    originalAmount,
    selectedAccountAddress,
  ]);

  return (
    <div className="edit-limit-order flex dir-c gap-1">
      <div className="modal__header edit-limit-order__header">
        <div className="edit-limit-order__summary flex ai-c w-full">
          <div className="edit-limit-order__summary-content flex ai-c grow">
            <div className="lp medium mr-3">
              <IconDenom denom={d.pair.assetBase.metadata.symbol} />
              <IconDenom denom={d.pair.assetQuote.metadata.symbol} />
            </div>
            <LabelSide {...d} />
            <div className="ml-0.5">
              <LabelType {...d} />
            </div>
            <div className="edit-limit-order__details grow ml-6">
              <table className="table table--sm condensed color-grey">
                <tbody>
                  {deviation !== null ? (
                    <tr>
                      <td>{t("deviation")}:</td>
                      <td className="text-right">
                        <EditOrderDeviationValue deviation={deviation} />
                      </td>
                    </tr>
                  ) : null}
                  <tr>
                    <td>{t("orderPrice")}:</td>
                    <td className="text-right">
                      <EditOrderPriceValue
                        price={orderPrice}
                        quoteSymbol={quoteSymbol}
                        quotePrice={quotePrice}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>{t("currentPrice")}:</td>
                    <td className="text-right">
                      <EditCurrentPriceValue
                        price={currentPrice}
                        quoteSymbol={quoteSymbol}
                        quotePrice={quotePrice}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <InputQuantity
        containerClassName="mt-2"
        amount={draftAmount}
        setAmount={commitAmount}
        onSliderChange={setDraftAmount}
      />

      <div
        className="edit-limit-order__settlement-row row pad--xs mt-2"
        style={{ marginLeft: "-0.5rem", marginRight: "-0.5rem" }}>
        <div className="col-6 flex">
          <div className="card px-2 py-1 flex fs-12 condensed color-grey grow">
            {t("required")}:{" "}
            <div className="color-white ml-a">
              {nFormatter(draftFunds > 0n ? draftFunds : 0n, 6)}{" "}
              {assetOffer.metadata.symbol}
            </div>
          </div>
        </div>
        <div className="col-6 flex">
          <div className="card px-2 py-1 flex fs-12 condensed color-grey grow">
            {t("withdrawnClaimed")}:{" "}
            <div className="ml-a text-right">
              <div className="color-white mb-0.5">
                {nFormatter(draftFunds < 0n ? -draftFunds : 0n, 6)}{" "}
                {assetOffer.metadata.symbol}
              </div>
              {BigInt(d.filled) ? (
                <div className="color-white">
                  {nFormatter(BigInt(d.filled), 6)} {assetAsk.metadata.symbol}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={hideModal}
          label={t("cancel")}
        />
        <div
          className="flex dir-c ai-e ml-a text-right"
          style={{
            minWidth: "6.375rem",
            whiteSpace: "nowrap",
          }}>
          <MsgProvider msg={msg}>
            {funds > 0n ? (
              <TxBalanceAccountButton
                key={txKey}
                required={{ asset: assetOffer, amount: funds }}
                disabled={draftAmount !== amount}
                hideSimulation
                className="button ml-1"
                label={t("confirm")}
                onSuccess={hideModal}
              />
            ) : (
              <TxButton
                key={txKey}
                disabled={draftAmount !== amount}
                hideSimulation
                className="button ml-1"
                label={t("confirm")}
                onSuccess={hideModal}
              />
            )}
          </MsgProvider>
        </div>
      </div>
    </div>
  );
};

const CancelLimitOrderModal: FC<{ hideModal: () => void }> = ({
  hideModal,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="modal__header">
        <h2>{t("cancelOrder")}</h2>
      </div>
      <p>{t("cancelOrderConfirmation")}</p>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={hideModal}
          label={t("cancel")}
        />
        <div className="block ml-a text-right">
          <TxButton
            className="button ml-1"
            label={t("confirm")}
            onSuccess={hideModal}
          />
        </div>
      </div>
    </>
  );
};
